// Articles — the little words (el, la, un, una…) that agree with their noun,
// the two contractions (al, del), the neuter "lo", and the cases where Spanish
// uses an article that English drops (and vice versa). A forms table closes it.
import { renderGuideSections } from "../ui/guideSections.js";

export function articlesView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Article Forms</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3"></th>
          <th class="py-2 px-3">Masc. sg</th>
          <th class="py-2 px-3">Fem. sg</th>
          <th class="py-2 px-3">Masc. pl</th>
          <th class="py-2 px-3">Fem. pl</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite (the)</td><td class="py-2 px-3">el</td><td class="py-2 px-3">la</td><td class="py-2 px-3">los</td><td class="py-2 px-3">las</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Indefinite (a / some)</td><td class="py-2 px-3">un</td><td class="py-2 px-3">una</td><td class="py-2 px-3">unos</td><td class="py-2 px-3">unas</td></tr>
          <tr><td class="py-2 pr-3 font-medium">Contractions</td><td class="py-2 px-3" colspan="4"><b>a + el = al</b> · <b>de + el = del</b> (no others)</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Neuter <b>lo</b> + adjective makes an abstract noun: <i>lo bueno</i> (the good part), <i>lo importante</i> (what's important).</p>
    </div>`;

  renderGuideSections(
    app,
    "Articles",
    "Articles are the small words in front of a noun — 'the', 'a', 'some'. In Spanish they agree with the noun in gender and number, and they show up in a few places English would leave blank.",
    [
      {
        title: "1. Definite vs indefinite",
        body: `
          <p class="text-sage-700 mb-2"><b>Definite</b> (el/la/los/las) = a specific, known thing — English <i>the</i>. <b>Indefinite</b> (un/una/unos/unas) = any one of a kind — English <i>a/an</i>; the plural <i>unos/unas</i> means <i>some / a few</i>.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>el</b> coche</i> = the (particular) car · <i><b>un</b> coche</i> = a car</li>
            <li><i><b>las</b> casas</i> = the houses · <i><b>unas</b> casas</i> = some houses</li>
          </ul>
        `,
      },
      {
        title: "2. The two contractions: al & del",
        body: `
          <p class="text-sage-700 mb-2">When <i>a</i> or <i>de</i> meets masculine singular <i>el</i>, they fuse. These are the <b>only</b> contractions in Spanish.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Voy <b>al</b> mercado</i> (a + el).</li>
            <li><i>Vengo <b>del</b> trabajo</i> (de + el).</li>
            <li>No contraction with <i>la, los, las</i>: <i>a la, de los, a las</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. The stressed-a rule",
        body: `
          <p class="text-sage-700 text-sm">A feminine noun starting with a <b>stressed a-/ha-</b> takes <b>el/un</b> in the singular for sound — <i>el agua, el águila, un hacha</i> — yet stays feminine, so adjectives agree feminine: <i>el agua <b>fría</b></i>. The plural goes back to normal: <i>las aguas</i>.</p>
        `,
      },
      {
        title: "4. When Spanish uses an article (and English doesn't)",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>General nouns:</b> <i><b>El</b> café es bueno</i> = Coffee is good.</li>
            <li><b>Languages & days:</b> <i>Hablo <b>el</b> español</i> · <i><b>el</b> lunes</i> = on Monday.</li>
            <li><b>Body parts & clothing</b> (not possessives): <i>Me duele <b>la</b> cabeza</i> = My head hurts.</li>
            <li><b>Titles (talking about):</b> <i><b>El</b> señor García</i> — but drop it when addressing them.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Omit</b> the indefinite article before unmodified professions: <i>Soy profesor</i> (I'm a teacher) — add it back if qualified: <i>Soy <b>un</b> profesor excelente</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
